/**
 * ActivityLog (8h.2, extended in G1b) — the merged, reverse-chronological feed
 * for a lead: rep notes, system events, follow-ups, and now EMAIL.
 *
 * ---------------------------------------------------------------------------
 * EMAIL LIVES HERE, NOT IN A TAB OF ITS OWN
 * ---------------------------------------------------------------------------
 * There was a separate Email tab and it was the wrong shape. It made a rep check
 * two surfaces to find out what had happened on one file, and it split "the
 * client emailed us" away from "we called them and logged it" when those are one
 * story told in order. One entry per message, in time order with everything
 * else, is the model every CRM that has solved this already uses.
 *
 * FOUR SOURCES MERGE HERE, and the merge was already doing two of them:
 *   activity_log   notes and system events
 *   follow_ups     scheduled and completed
 *   email_messages one row per synced message, in or out
 *   email_log      the CRM's own Resend sends (estimates, invoices…)
 *
 * ---------------------------------------------------------------------------
 * A SEND APPEARS ONCE. NEVER TWICE.
 * ---------------------------------------------------------------------------
 * Every CRM send writes BOTH an email_log row and an activity_log line in prose
 * ("Estimate emailed to x@y.ca by Daryl"). Since migration 040 that line carries
 * the email_log id, so the rule here is exact rather than a guess: an activity
 * entry naming a send is ENRICHED with its delivery state and portal link; a
 * send that no entry names is shown on its own. The second case is not an edge
 * case — a FAILED send never wrote an activity line at all, because logSystem
 * sits inside `if (result.ok)` — so those rows are how a failure becomes visible
 * on the file instead of only in the Audit Report.
 *
 * ---------------------------------------------------------------------------
 * TEXTS AND CALLS LIVE HERE TOO (RingCentral)
 * ---------------------------------------------------------------------------
 * Texts are ONE entry per message, straight from ringcentral_messages: the
 * whole body, in or out, who sent it, when, its status. The activity_log line a
 * text also writes (for Activity Stats) is CLAIMED by
 * that entry and not shown on its own — the email_log rule again. The texts are
 * the CLIENT'S: every text with this lead's number, including ones filed on
 * their other jobs (tagged "on job ####"). A RingCentral call line shows as a
 * call: in / out, missed.
 *
 * Calls, texts and emails are COMMUNICATION and look it — a channel icon, an
 * in / out marker and a tinted row (--crm-comm-* in theme.css). Notes stay
 * plain.
 *
 * ---------------------------------------------------------------------------
 * FILTER CHIPS, BECAUSE 122 EMAILS WOULD BURY EVERYTHING ELSE
 * ---------------------------------------------------------------------------
 * A long-running client file has more mail on it than notes, and a rep looking
 * for what a colleague wrote down should not have to scroll past a quarter of
 * correspondence. All / Communication (calls, texts, emails) / Notes,
 * defaulting to All; the last choice is remembered per user in this browser.
 *
 * Notes can be PINNED (V6.1): pinned notes sort to the top (most-recently-pinned
 * first), get an amber left border + PINNED badge. System entries, email and
 * follow-up rows cannot be pinned. Follow-ups carry a separate "pin to calendar".
 */

import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

/**
 * Messages → "open this entry" requests already carried out, by request
 * (`leadId:messageId:n` — n goes up on every click in Messages). Module-level so
 * a remount of the feed (another tab and back) does not scroll there again.
 *
 * THE BUG THIS PREVENTS: App keeps focusMessage for as long as that lead is
 * open, and the scroll effects below depended on the feed's data. Every 30 s
 * texts refresh (and any activity / email refresh) re-ran them, so the page
 * jumped back to the entry — a new inbound text, i.e. the TOP of the feed —
 * while the rep was reading further down.
 */
const handledFocus = new Set<string>();

/** The element that scrolls the lead page (LeadDetailPage's overflowY:auto div). */
function scrollParentOf(el: HTMLElement | null): HTMLElement | null {
  for (let p = el?.parentElement ?? null; p; p = p.parentElement) {
    const oy = getComputedStyle(p).overflowY;
    if ((oy === 'auto' || oy === 'scroll') && p.scrollHeight > p.clientHeight) return p;
  }
  return null;
}
import { CRM, tintOf } from '../lib/theme';
import { draftKey, readDraft, writeDraft, clearDraft, useUnsavedGuard } from '../hooks/useDraftText';
import { useApi, useApiClient } from '../hooks/useApi';
import { useAuth } from '../contexts/AuthContext';
import { formatDate } from '../lib/utils';
import { userTz, todayInTz, formatDateTimeInTz } from '../lib/time';
import { Icons } from '../lib/icons';
import { activityTypeLabel, emailTypeLabel, sendStatus } from '../lib/feedLabels';
import { EmailBody, Attachments } from './EmailBody';
import { ComposeEmail, ReplyContext } from './ComposeEmail';
import { useAction } from '../hooks/useAction';
import { ringCentralEnabled } from '../lib/ringcentral';
import { TextCompose } from './TextCompose';
import type { LeadTexts, LeadTextMessage } from '../services/api';

const C = { ...CRM, mono: "'DM Mono', monospace" };

type FeedFilter = 'all' | 'comm' | 'notes';

/**
 * COMMUNICATION ROWS — calls, texts and emails — share one look so they stand
 * out from notes: the channel's tint and border, its colour on the left edge,
 * and a header of channel icon + IN / OUT marker. Colours: --crm-comm-* in
 * theme.css.
 */
type Channel = 'call' | 'text' | 'email';
const COMM: Record<Channel, { fg: string; tint: string; line: string; Icon: React.ComponentType<any>; label: string }> = {
  call: { fg: 'var(--crm-comm-call)', tint: 'var(--crm-comm-call-tint)', line: 'var(--crm-comm-call-line)', Icon: Icons.phone, label: 'Call' },
  text: { fg: 'var(--crm-comm-text)', tint: 'var(--crm-comm-text-tint)', line: 'var(--crm-comm-text-line)', Icon: Icons.message, label: 'Text' },
  email: { fg: 'var(--crm-comm-email)', tint: 'var(--crm-comm-email-tint)', line: 'var(--crm-comm-email-line)', Icon: Icons.email, label: 'Email' },
};

const commBox = (ch: Channel, extra?: React.CSSProperties): React.CSSProperties => ({
  background: COMM[ch].tint, border: `1px solid ${COMM[ch].line}`, borderLeft: `3px solid ${COMM[ch].fg}`,
  borderRadius: '8px', padding: '9px 11px', minWidth: 0, ...extra,
});

/** Channel icon + label + IN/OUT marker. `dir` null = no marker (unknown). */
function CommTag({ ch, dir, label }: { ch: Channel; dir: 'in' | 'out' | null; label?: string }) {
  const c = COMM[ch];
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', flexShrink: 0 }}>
      <span style={{ display: 'inline-flex', color: c.fg }}><c.Icon size={13} /></span>
      <span style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', color: c.fg, letterSpacing: '0.4px' }}>{label ?? c.label}</span>
      {dir && (
        <span title={dir === 'in' ? 'From the client' : 'To the client'}
              style={{ fontSize: '9px', fontWeight: 700, color: c.fg, border: `1px solid ${c.line}`, background: C.panel, borderRadius: '4px', padding: '0 5px', lineHeight: '15px' }}>
          {dir === 'in' ? '← IN' : 'OUT →'}
        </span>
      )}
    </span>
  );
}

function MarkReadBtn({ onClick, fg }: { onClick: () => void; fg: string }) {
  return (
    <button onClick={(e) => { e.stopPropagation(); onClick(); }} title="Mark read (same as in Messages)"
            style={{ marginLeft: 'auto', padding: '2px 8px', background: C.panel, border: `1px solid ${fg}`, color: fg, borderRadius: '6px', cursor: 'pointer', fontSize: '10.5px', fontWeight: 600, whiteSpace: 'nowrap' }}>
      Mark read
    </button>
  );
}

/**
 * What a rep logs BY HAND. Call and text only.
 *
 * Email was a chip here and is not any more: emails are counted automatically
 * from the messages themselves — anything outbound to a matched lead, whether
 * composed in the CRM or sent from a rep's own Gmail. Asking someone to tick a
 * box recording an email they have just sent is asking them to do the work
 * twice, and the one who forgets looks less productive than the one who does
 * not, which is the opposite of what the figure is for.
 *
 * Calls and texts stay manual until RingCentral starts reporting them; see
 * contact_source and the cutover date in migration 043.
 */
const METHODS = [
  { key: 'call', label: 'Call', Icon: Icons.phone },
  { key: 'text', label: 'Text', Icon: Icons.message },
];

interface ActivityLogProps {
  leadId: string;
  users?: any[];
  // Bumped by a parent (e.g. after emailing an estimate) to force a refetch so a
  // newly-logged system note appears without navigating away.
  refreshSignal?: number;
  /** The lead's primary address, prefilled into a new email's To field (G1c). */
  leadEmail?: string | null;
  /** The lead's phone, shown on the Text box (RingCentral). */
  leadPhone?: string | null;
  /** G1d: scroll to one email and expand it (from the Messages dropdown). */
  focusMessage?: { messageId: string; n: number } | null;
  /** The phone lead header's Email button: open a new email here, scrolled into
   *  view, then onComposeHandled() clears the request. */
  composeRequest?: number | null;
  onComposeHandled?: () => void;
}

export function ActivityLog({ leadId, users, refreshSignal, leadEmail, leadPhone, focusMessage, composeRequest, onComposeHandled }: ActivityLogProps) {
  const api = useApiClient();
  const { user } = useAuth();
  const { data: activityRaw, error: activityError, refetch: refetchActivity, refresh: refreshActivityRaw } = useApi<any[]>(
    // Sales lines only: dispatch lines (contractor pay, approvals, weight sync…)
    // are in the Dispatch tab's History popup (decision m).
    async () => (await api.getActivityLog(leadId, 'sales')).data || [],
    { dependencies: [leadId, refreshSignal] }
  );
  // N3 3a: the lead file shows EVERY follow-up on this lead (all assignees), not
  // just the viewer's. Badges/dropdown stay personal (a different endpoint).
  const { data: followupsRaw, error: followupsError, refetch: refetchFollowups, refresh: refreshFollowupsRaw } = useApi<any[]>(
    async () => (await api.getLeadFollowUps(leadId)).data || [],
    { dependencies: [leadId] }
  );
  // G1b: the two email sources. One call; the server returns flat messages and
  // every CRM send, linked or not.
  const { data: emailRaw, error: emailError, refetch: refetchEmail, refresh: refreshEmailRaw } = useApi<any>(
    async () => (await api.getLeadEmails(leadId)).data,
    { dependencies: [leadId, refreshSignal] }
  );
  // RingCentral texts with this client's number, from every file (only when
  // RingCentral is on). Re-read every 30 s so a reply that sync brings in shows.
  const { data: textsData, error: textsError, refetch: refetchTexts, refresh: refreshTextsRaw } = useApi<LeadTexts | null>(
    async () => (ringCentralEnabled ? (await api.getLeadTexts(leadId)).data ?? null : null),
    { dependencies: [leadId, refreshSignal] }
  );

  /**
   * AFTER A SAVE, NOTHING HERE BLANKS OR WAITS.
   *
   *   - Writes refresh QUIETLY (useApi.refresh): the log stays on screen until
   *     the new one replaces it, and a failed refresh keeps it. refetch*() is
   *     kept only for the error panel's own Retry, where showing "loading" is
   *     the truth.
   *   - Pin / unpin (notes and follow-ups) and mark-read show at once through
   *     the overrides below, which are laid over the server rows. A failure takes
   *     the override back off and says so; a success leaves it until the refresh
   *     brings the same value back, then drops it.
   *   - Mark complete was already optimistic (optimisticDone); it now refreshes
   *     quietly too.
   */
  const [pinOverride, setPinOverride] = useState<Record<string, boolean>>({});      // activity id -> pinned
  const [fuPinOverride, setFuPinOverride] = useState<Record<string, boolean>>({});  // follow-up id -> pinned
  const [seenOverride, setSeenOverride] = useState<Record<string, string>>({});     // linkId -> seenAt

  const activity = useMemo(() => (activityRaw ?? null) && activityRaw!.map((a) =>
    a.id in pinOverride ? { ...a, pinned: pinOverride[a.id], pinned_at: pinOverride[a.id] ? (a.pinned_at ?? new Date().toISOString()) : null } : a),
  [activityRaw, pinOverride]);
  const allFollowups = useMemo(() => (followupsRaw ?? null) && followupsRaw!.map((f) =>
    f.id in fuPinOverride ? { ...f, pinned: fuPinOverride[f.id] } : f),
  [followupsRaw, fuPinOverride]);
  const emailData = useMemo(() => {
    if (!emailRaw || !Object.keys(seenOverride).length) return emailRaw;
    return {
      ...emailRaw,
      messages: (emailRaw.messages ?? []).map((m: any) =>
        m.linkId && seenOverride[m.linkId] && !m.seenAt ? { ...m, seenAt: seenOverride[m.linkId] } : m),
    };
  }, [emailRaw, seenOverride]);

  const quiet = (f: () => Promise<void>, what: string) => () => f().catch((e) => console.error(`${what} refresh failed:`, e));
  const refreshActivity = quiet(refreshActivityRaw, 'Activity');
  const refreshFollowups = quiet(refreshFollowupsRaw, 'Follow-ups');

  // An inbound text / missed call, marked read from the file — the same mark
  // Messages clears (lib/ringcentral/inbox). Messages' badge is told at once.
  const [readRc, setReadRc] = useState<Set<string>>(() => new Set());
  const markRcRead = async (kind: 'text' | 'call', id: string) => {
    setReadRc((s) => new Set(s).add(`${kind}:${id}`));
    try {
      await api.markRingCentralSeen(kind, id);
      window.dispatchEvent(new Event('crm:badges-changed'));
      if (kind === 'text') refreshTexts(); else refreshActivity();
    } catch (e) {
      console.error('Could not mark it read:', e);
      setReadRc((s) => { const n = new Set(s); n.delete(`${kind}:${id}`); return n; });
    }
  };

  // A RingCentral call logged or noted on THIS lead (RingCentralDock): show
  // its feed line and any follow-up without a reload.
  useEffect(() => {
    const onLogged = (e: Event) => {
      if ((e as CustomEvent).detail?.leadId !== leadId) return;
      refreshActivity();
      refreshFollowups();
      refreshTexts();
    };
    window.addEventListener('ringcentral:call-logged', onLogged);
    return () => window.removeEventListener('ringcentral:call-logged', onLogged);
  });
  const refreshEmail = quiet(refreshEmailRaw, 'Email');
  const refreshTexts = quiet(refreshTextsRaw, 'Texts');
  useEffect(() => {
    if (!ringCentralEnabled) return;
    const id = window.setInterval(() => refreshTexts(), 30_000);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId]);
  const dropKey = <T,>(set: React.Dispatch<React.SetStateAction<Record<string, T>>>, k: string) =>
    set((m) => { const n = { ...m }; delete n[k]; return n; });
  // Asked once, so the Email button can explain itself instead of failing on
  // submit — the allowlist case especially, where a staging send would reach a
  // real client from a real rep's address.
  const { data: capabilityData, error: capabilityError } = useApi<any>(
    async () => (await api.getEmailCapability()).data
  );

  /**
   * A capability we could not LOAD is not a capability to send.
   *
   * The endpoint is new, and a Railway deploy puts the frontend up before the
   * backend — which is exactly how the feed once showed "Emails 0" against a
   * 404. Left as null this reads as "no objection" and the Email button opens a
   * panel that can only fail on submit, after the rep has written the message.
   * An unreachable check is therefore a refusal with the reason said plainly.
   */
  const capability = capabilityData ?? (capabilityError
    ? {
        canSend: false,
        reason:
          'The CRM cannot reach the email service to check whether you can send. ' +
          'If the app has just updated, give it a minute and reload — the server may still be ' +
          `restarting. (${capabilityError})`,
        fromAddress: '', fromName: '',
      }
    : null);
  const tz = userTz(user);
  // Optimistic completion overlay (id -> true) with rollback on failure.
  const [optimisticDone, setOptimisticDone] = useState<Record<string, boolean>>({});
  // Which slice of the feed is on screen. 'all' by default — the filter exists
  // so mail cannot bury the notes, not to hide anything by default.
  // Remembered per user in this browser (a convenience: unreadable storage just
  // means All).
  const filterKey = `crm.salesFeedFilter.${user?.id ?? 'anon'}`;
  const [filter, setFilter] = useState<FeedFilter>(() => {
    try {
      const v = window.localStorage.getItem(filterKey);
      return v === 'comm' || v === 'notes' ? v : 'all';
    } catch { return 'all'; }
  });
  const chooseFilter = (f: FeedFilter) => {
    setFilter(f);
    try { window.localStorage.setItem(filterKey, f); } catch { /* not kept; fine */ }
  };
  const [openEmail, setOpenEmail] = useState<string | null>(null);
  // null = closed. { message: null } = a new email; otherwise a reply.
  const [compose, setCompose] = useState<{ reply: ReplyContext | null } | null>(null);
  const [textOpen, setTextOpen] = useState(false);
  const composeRef = React.useRef<HTMLDivElement | null>(null);
  const scrollToCompose = React.useRef(false);

  /**
   * WHICH SOURCES FAILED, so the feed can say so instead of rendering a
   * confident zero.
   *
   * A feed that shows "Emails 0" when the email request errored is worse than
   * one that shows nothing: it is a positive claim that the client has never
   * written, made by a component that does not know. That is exactly what
   * happened when the frontend deployed ahead of the backend — the endpoint
   * 404ed and the chip read zero.
   */
  const failed = [
    activityError ? { label: 'Notes and activity', error: activityError, retry: refetchActivity } : null,
    emailError ? { label: 'Emails', error: emailError, retry: refetchEmail } : null,
    followupsError ? { label: 'Follow-ups', error: followupsError, retry: refetchFollowups } : null,
    textsError ? { label: 'Texts', error: textsError, retry: refetchTexts } : null,
  ].filter(Boolean) as { label: string; error: string; retry: () => void }[];

  /**
   * Land on the message the Messages dropdown asked for.
   *
   * It waits for the email data, because the row cannot be scrolled to before it
   * exists — clicking from the top bar navigates and fetches at the same time,
   * and without the data in the dependency list this fires once against an empty
   * feed and never again. It does NOT mark anything read: that is a button.
   */
  // A text or a missed call from Messages: 'text:<id>' / 'call:<id>'. Waits for
  // the texts / the feed, then scrolls to the entry (rc-text-… / rc-call-…).
  // ONCE per click in Messages (handledFocus): the data dependencies are only
  // there to wait for the entry to exist, never to scroll to it again.
  const focusKey = focusMessage ? `${leadId}:${focusMessage.messageId}:${focusMessage.n}` : '';
  useEffect(() => {
    const target = focusMessage?.messageId ?? '';
    const m = /^(text|call):(.+)$/.exec(target);
    if (!m || handledFocus.has(focusKey)) return;
    if (m[1] === 'text' ? !textsData : !activityRaw) return;
    handledFocus.add(focusKey);
    setFilter('all');
    const t = window.setTimeout(() => {
      document.getElementById(`rc-${m[1]}-${m[2]}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 60);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusKey, textsData, activityRaw]);

  useEffect(() => {
    if (!focusMessage?.messageId || !emailData) return;
    if (/^(text|call):/.test(focusMessage.messageId)) return;
    if (handledFocus.has(focusKey)) return;
    handledFocus.add(focusKey);
    const rowId = `em-${focusMessage.messageId}`;
    setFilter('all');
    setOpenEmail(rowId);
    const t = window.setTimeout(() => {
      document.getElementById(`email-${focusMessage.messageId}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 60);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusKey, emailData]);

  /**
   * EXPLICIT, ALWAYS, AND ONE AT A TIME.
   *
   * Nothing here marks a message read as a side effect of being looked at — not
   * expanding it, not scrolling to it from the Messages dropdown. There is no
   * bulk version either: the mark is shared with everyone viewing the file, so
   * clearing a file's worth in one press would answer on their behalf as well as
   * your own, and "read" would stop meaning anybody read it.
   */
  const markRead = async (linkId: string) => {
    setSeenOverride((m) => ({ ...m, [linkId]: new Date().toISOString() }));
    try { await api.markMessageSeen(linkId); await refreshEmail(); }
    catch (e: any) { setErr(e?.message || 'Could not mark that read.'); }
    finally { dropKey(setSeenOverride, linkId); }
  };

  // The note being written. Not autosaved — "Add to log" sends it — but backed
  // up to this browser on every keystroke and put back if the file is reopened
  // (a closed tab, a crash), and the tab asks before closing while it has text.
  const noteKey = draftKey(leadId, 'activity_note');
  const [text, setTextState] = useState('');
  const [noteRestoredAt, setNoteRestoredAt] = useState<number | null>(null);
  const setText = (v: string) => {
    setTextState(v);
    if (v.trim()) writeDraft(noteKey, v, ''); else clearDraft(noteKey);
  };
  useEffect(() => {
    const b = readDraft(noteKey);
    setTextState(b?.v ?? '');
    setNoteRestoredAt(b ? b.at : null);
  }, [noteKey]);
  useUnsavedGuard(text.trim() !== '');
  const [methods, setMethods] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  // Fix B: request failures on the sales page surface INLINE here, never as a
  // blocking window.alert (a 403 must not interrupt the page).
  const [err, setErr] = useState<string | null>(null);

  // The phone header's Email button (via LeadDetailPage): the same as pressing
  // Email below, then brought into view.
  useEffect(() => {
    if (!composeRequest) return;
    if (capability && !capability.canSend) setErr(capability.reason);
    else { scrollToCompose.current = true; setCompose({ reply: null }); }
    onComposeHandled?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [composeRequest]);
  useEffect(() => {
    if (!compose || !scrollToCompose.current) return;
    scrollToCompose.current = false;
    composeRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [compose]);
  const [showFu, setShowFu] = useState(false);
  // Inline "schedule a follow-up alongside this note" state.
  const [scheduleOn, setScheduleOn] = useState(false);
  const myToday = todayInTz(userTz(user)); // follow-up "today" in the rep's own zone (N2b)
  const [fu, setFu] = useState({ follow_up_date: myToday, follow_up_time: '', assigned_to_user_id: user?.id ?? '', pinned: false });

  const userName = (id: string | null | undefined) => {
    const u = users?.find((x) => x.id === id);
    return u ? `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() : '';
  };

  const toggleMethod = (k: string) => setMethods((m) => (m.includes(k) ? m.filter((x) => x !== k) : [...m, k]));

  const resetFu = () => setFu({ follow_up_date: myToday, follow_up_time: '', assigned_to_user_id: user?.id ?? '', pinned: false });

  // "Add to log" — creates a note and/or the inline follow-up in one action.
  // A follow-up without a note is valid; if neither would be created, no-op.
  const submitGuard = useAction(); // one at a time: a second click is ignored
  const submit = () => submitGuard.run(() => submitNow());
  const submitNow = async () => {
    const hasNote = text.trim() !== '';
    const hasFu = scheduleOn && !!fu.follow_up_date;
    if (!hasNote && !hasFu) return;
    setBusy(true);
    setErr(null);
    try {
      if (hasNote) {
        await api.createActivityLog({ lead_id: leadId, activity_type: 'note', description: text.trim(), contact_methods: methods });
      }
      if (hasFu) {
        await api.createFollowUp({
          lead_id: leadId,
          assigned_to_user_id: fu.assigned_to_user_id || user?.id,
          follow_up_date: fu.follow_up_date,
          follow_up_time: fu.follow_up_time || undefined,
          // Default the follow-up's context to the note text when present.
          description: hasNote ? text.trim() : undefined,
          pinned: fu.pinned,
        });
      }
      setText('');
      setNoteRestoredAt(null);
      setMethods([]);
      setScheduleOn(false);
      resetFu();
      refreshActivity();
      refreshFollowups();
    } catch (e: any) {
      setErr(e?.message || 'Failed to add to log');
    } finally {
      setBusy(false);
    }
  };

  const togglePin = async (a: any) => {
    setPinOverride((m) => ({ ...m, [a.id]: !a.pinned }));
    try { await api.setActivityPinned(a.id, !a.pinned); await refreshActivity(); }
    catch (e: any) { setErr(e?.message || 'Could not change the pin.'); }
    finally { dropKey(setPinOverride, a.id); }
  };
  const toggleFollowupPin = async (f: any) => {
    setFuPinOverride((m) => ({ ...m, [f.id]: !f.pinned }));
    try { await api.setFollowUpPinned(f.id, !f.pinned); await refreshFollowups(); }
    catch (e: any) { setErr(e?.message || 'Could not change the pin.'); }
    finally { dropKey(setFuPinOverride, f.id); }
  };

  // Mark complete — optimistic, rolls back on failure with an actionable message.
  const completeFu = async (f: any) => {
    setErr(null);
    setOptimisticDone((m) => ({ ...m, [f.id]: true }));
    try {
      await api.completeFollowUp(f.id);
      await refreshFollowups();
      refreshActivity(); // surface the "Follow-up completed" entry
    } catch (e: any) {
      setOptimisticDone((m) => { const n = { ...m }; delete n[f.id]; return n; }); // rollback
      setErr(`Couldn't mark this follow-up complete: ${e?.message || 'network error'}. Please try again.`);
    }
  };

  // Reassign an open follow-up to another user (CHANGE C edit path).
  const reassignFu = async (f: any, userId: string) => {
    if (!userId || userId === f.assigned_to_user_id) return;
    setErr(null);
    try {
      await api.updateFollowUp(f.id, { assigned_to_user_id: userId });
      await refreshFollowups();
      refreshActivity();
    } catch (e: any) {
      setErr(`Couldn't reassign this follow-up: ${e?.message || 'network error'}. You may only reassign follow-ups assigned to you.`);
    }
  };
  // Standalone modal save (no note required).
  const scheduleFollowUpGuard = useAction(); // one at a time: a second click is ignored
  const scheduleFollowUp = (data: any) => scheduleFollowUpGuard.run(() => scheduleFollowUpNow(data));
  const scheduleFollowUpNow = async (data: any) => {
    setBusy(true);
    setErr(null);
    try {
      await api.createFollowUp({ lead_id: leadId, assigned_to_user_id: data.assigned_to_user_id || user?.id, follow_up_date: data.follow_up_date, follow_up_time: data.follow_up_time || undefined, description: data.description || undefined, pinned: data.pinned });
      setShowFu(false);
      refreshFollowups();
      refreshActivity();
    } catch (e: any) {
      setErr(e?.message || 'Failed to schedule');
    } finally {
      setBusy(false);
    }
  };

  // Merge all four sources; pinned notes first (by pinned_at desc), then
  // everything else reverse-chronological.
  const rows = useMemo(() => {
    const followups = (allFollowups ?? []).filter((f) => f.lead_id === leadId);
    const messages: any[] = emailData?.messages ?? [];
    const sends: any[] = emailData?.sends ?? [];

    // The join that guarantees one row per send. An activity entry carrying an
    // email_log_id IS that send, so it is enriched rather than duplicated; the
    // ids it claims are then off the table for a row of their own.
    const sendById = new Map(sends.map((x) => [String(x.id), x]));
    const claimed = new Set(
      (activity ?? []).map((a) => String(a.email_log_id ?? '')).filter(Boolean)
    );

    // A text's activity line is the text's: it is not shown.
    // Only a line whose text IS in the list is hidden: if the texts could not be
    // loaded (or RingCentral is off) the line shows as it is, never nothing.
    const texts: LeadTextMessage[] = textsData?.messages ?? [];
    const textIds = new Set(texts.map((t) => String(t.id)));

    const merged = [
      ...(activity ?? [])
        .filter((a) => !(a.ringcentral_message_id && textIds.has(String(a.ringcentral_message_id))))
        .map((a) => {
          const send = a.email_log_id ? sendById.get(String(a.email_log_id)) ?? null : null;
          const kind = send ? ('send' as const) : a.ringcentral_call_id ? ('call' as const) : ('activity' as const);
          return { kind, id: a.id, ts: a.created_at, data: a, send };
        }),
      ...texts.map((t) => ({
        kind: 'text' as const, id: `tx-${t.id}`, ts: t.at,
        data: t, send: null,
      })),
      ...followups.map((f) => ({
        kind: 'followup' as const, id: `fu-${f.id}`,
        ts: `${f.follow_up_date}T${f.follow_up_time || '00:00'}`, data: f, send: null,
      })),
      ...messages.map((m) => ({ kind: 'email' as const, id: `em-${m.id}`, ts: m.at, data: m, send: null })),
      // Only the orphans. A send already named by an activity entry is above.
      ...sends
        .filter((x) => !claimed.has(String(x.id)))
        .map((x) => ({ kind: 'send' as const, id: `sn-${x.id}`, ts: x.at, data: null, send: x })),
    ];

    const isPinnedNote = (r: any) => r.kind === 'activity' && r.data?.activity_type === 'note' && r.data?.pinned;
    merged.sort((a, b) => {
      const ap = isPinnedNote(a), bp = isPinnedNote(b);
      if (ap && bp) return new Date(b.data.pinned_at ?? 0).getTime() - new Date(a.data.pinned_at ?? 0).getTime();
      if (ap) return -1;
      if (bp) return 1;
      return new Date(b.ts ?? 0).getTime() - new Date(a.ts ?? 0).getTime();
    });
    return merged;
  }, [activity, allFollowups, emailData, textsData, leadId]);

  // Which chip a row belongs to. Calls, texts and emails (a CRM send counts as
  // email wherever it came from) are Communication; a rep's typed note is a
  // Note; system lines and follow-ups show under All only.
  const bucketOf = (r: any): 'comm' | 'notes' | 'other' =>
    r.kind === 'email' || r.kind === 'send' || r.kind === 'text' || r.kind === 'call' ? 'comm'
    : r.kind === 'activity' && r.data?.activity_type === 'note' ? 'notes'
    : 'other';

  const visibleRows = useMemo(
    () => (filter === 'all' ? rows : rows.filter((r) => bucketOf(r) === filter)),
    [rows, filter]
  );

  /**
   * KEEP THE READER'S PLACE. The feed is newest first, so a refresh that brings
   * a new text, email or note inserts it ABOVE whoever is reading further down,
   * pushing what they were reading out of view. Chrome compensates (scroll
   * anchoring); Safari — every iPhone — does not. So, everywhere: the top
   * visible row is remembered as the rep scrolls, and after the rows change
   * the page is moved by however far that row moved. At the top of the page
   * there is no anchor and new entries simply appear. overflowAnchor:none on
   * the feed stops Chrome adjusting a second time.
   */
  const feedRef = useRef<HTMLDivElement | null>(null);
  const anchor = useRef<{ id: string; top: number } | null>(null);
  const readAnchor = () => {
    const feed = feedRef.current;
    const scroller = scrollParentOf(feed);
    if (!feed || !scroller) { anchor.current = null; return; }
    const viewTop = scroller.getBoundingClientRect().top;
    // The top of the feed in view: a new entry lands where the rep can see it,
    // so there is nothing to keep.
    if (feed.getBoundingClientRect().top >= viewTop) { anchor.current = null; return; }
    // Otherwise the first row still (partly) in view.
    for (const el of Array.from(feed.children) as HTMLElement[]) {
      const id = el.dataset.feedId;
      if (!id) continue;
      const r = el.getBoundingClientRect();
      if (r.bottom > viewTop + 1) { anchor.current = { id, top: r.top }; return; }
    }
    anchor.current = null;
  };
  useEffect(() => {
    const scroller = scrollParentOf(feedRef.current);
    if (!scroller) return;
    const onScroll = () => readAnchor();
    scroller.addEventListener('scroll', onScroll, { passive: true });
    return () => scroller.removeEventListener('scroll', onScroll);
    // Re-attached when the feed first has rows (the scroller may not scroll before).
  }, [visibleRows.length > 0]);   // eslint-disable-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    const a = anchor.current;
    const feed = feedRef.current;
    if (a && feed) {
      const el = Array.from(feed.children).find((c) => (c as HTMLElement).dataset.feedId === a.id) as HTMLElement | undefined;
      const scroller = scrollParentOf(feed);
      if (el && scroller) {
        const delta = el.getBoundingClientRect().top - a.top;
        if (Math.abs(delta) > 0.5) scroller.scrollTop += delta;
      }
    }
    readAnchor();
  }, [visibleRows]);   // eslint-disable-line react-hooks/exhaustive-deps

  const counts = useMemo(() => {
    const c = { all: rows.length, comm: 0, notes: 0, other: 0 };
    for (const r of rows) c[bucketOf(r)]++;
    return c;
  }, [rows]);

  const chip = (active: boolean): React.CSSProperties => ({
    display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 10px', borderRadius: '999px', cursor: 'pointer', fontSize: '11px',
    background: active ? `${C.accentTint}` : C.panel2, border: `1px solid ${active ? C.accentLine : C.border}`, color: active ? C.accent : C.muted,
  });

  return (
    <div>
      {/* Inline error (Fix B) — never a blocking alert. */}
      {err && (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', background: `${C.redTint}`, border: `1px solid ${C.redSoftLine}`, color: C.red, borderRadius: '8px', padding: '8px 11px', marginBottom: '10px', fontSize: '12px' }}>
          <span style={{ flex: 1 }}>{err}</span>
          <button onClick={() => setErr(null)} style={{ background: 'none', border: 'none', color: C.red, cursor: 'pointer', padding: 0, lineHeight: 1 }} aria-label="Dismiss"><Icons.close size={14} /></button>
        </div>
      )}

      {/* Composer */}
      <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: '10px', padding: '12px', marginBottom: '12px' }}>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Add a note…"
          style={{ width: '100%', minHeight: '58px', background: C.panel2, border: `1px solid ${C.border}`, color: C.text, borderRadius: '7px', padding: '8px 10px', fontSize: '12px', resize: 'vertical', boxSizing: 'border-box' }}
        />
        {noteRestoredAt != null && text.trim() !== '' && (
          <div style={{ fontSize: '11px', color: C.muted, marginTop: '4px' }}>
            Restored an unsent note from {new Date(noteRestoredAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}.{' '}
            <button type="button" onClick={() => { setText(''); setNoteRestoredAt(null); }} style={{ background: 'none', border: 'none', padding: 0, color: C.accent, cursor: 'pointer', fontSize: '11px' }}>Discard</button>
          </div>
        )}
        {/* Contact-method chips */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px', flexWrap: 'wrap' }}>
          {METHODS.map((m) => (
            <span key={m.key} onClick={() => toggleMethod(m.key)} style={chip(methods.includes(m.key))}><span style={{display:"inline-flex",alignItems:"center",gap:"3px"}}><m.Icon size={13} /> {m.label}</span></span>
          ))}
        </div>

        {/* Inline "schedule a follow-up alongside this note" */}
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '10px', cursor: 'pointer', fontSize: '12px', color: C.text }}>
          <input type="checkbox" checked={scheduleOn} onChange={(e) => setScheduleOn(e.target.checked)} />
          Schedule a follow-up
        </label>
        {scheduleOn && (
          <div style={{ marginTop: '8px', padding: '10px', background: C.panel2, border: `1px solid ${C.border}`, borderRadius: '8px', display: 'grid', gap: '8px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}><span style={{ fontSize: '10px', color: C.muted }}>Date *</span><input type="date" value={fu.follow_up_date} onChange={(e) => setFu({ ...fu, follow_up_date: e.target.value })} style={fieldInput} /></label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}><span style={{ fontSize: '10px', color: C.muted }}>Time</span><input type="time" value={fu.follow_up_time} onChange={(e) => setFu({ ...fu, follow_up_time: e.target.value })} style={fieldInput} /></label>
            </div>
            <label style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
              <span style={{ fontSize: '10px', color: C.muted }}>Assigned rep</span>
              <select value={fu.assigned_to_user_id} onChange={(e) => setFu({ ...fu, assigned_to_user_id: e.target.value })} style={fieldInput}>
                {(users ?? []).map((u) => <option key={u.id} value={u.id}>{`${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() || u.email}</option>)}
              </select>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input type="checkbox" checked={fu.pinned} onChange={(e) => setFu({ ...fu, pinned: e.target.checked })} />
              <span style={{ fontSize: '12px', color: C.text }}>Pin to calendar <span style={{ fontSize: '10px', color: C.dim }}>· appears on the calendar for visibility</span></span>
            </label>
            <div style={{ fontSize: '10px', color: C.dim }}>Description defaults to the note text above when left blank.</div>
          </div>
        )}

        {/* Actions — they wrap on a phone rather than run off the screen. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '10px', flexWrap: 'wrap' }}>
          <button
            onClick={() => setShowFu(true)}
            disabled={busy}
            style={{ padding: '7px 12px', background: C.panel2, color: C.text, border: `1px solid ${C.border}`, borderRadius: '7px', cursor: 'pointer', fontSize: '12px' }}
          >
            Schedule follow-up…
          </button>
          <button
            onClick={submit}
            disabled={busy || (!text.trim() && !(scheduleOn && fu.follow_up_date))}
            style={{ marginLeft: 'auto', padding: '7px 14px', background: C.accentFill, color: 'var(--crm-on-fill)', border: 'none', borderRadius: '7px', cursor: 'pointer', fontSize: '12px', fontWeight: 600, opacity: busy || (!text.trim() && !(scheduleOn && fu.follow_up_date)) ? 0.5 : 1 }}
          >
            Add to log
          </button>
          {/* Its OWN button and its own panel. Adding email as a third mode of
              "Add to log" would make one control do three unrelated things and
              make all three harder to explain. When sending is unavailable the
              button stays visible and says why on hover and on click — a missing
              button raises "where has email gone", an explained one does not. */}
          <button
            onClick={() => {
              if (capability && !capability.canSend) { setErr(capability.reason); return; }
              setCompose({ reply: null });
            }}
            disabled={busy}
            title={capability && !capability.canSend ? capability.reason : 'Email this client from your own Gmail'}
            style={{
              padding: '7px 14px',
              background: C.panel2,
              color: capability && !capability.canSend ? C.dim : C.text,
              border: `1px solid ${capability && !capability.canSend ? C.border : `${C.accentSoftLine}`}`,
              borderRadius: '7px', cursor: 'pointer', fontSize: '12px', fontWeight: 600,
              display: 'inline-flex', alignItems: 'center', gap: '6px',
            }}
          >
            <Icons.email size={14} /> Email
          </button>
          {/* Text from the main number (RingCentral), beside Email. */}
          {ringCentralEnabled && (
            <button
              onClick={() => setTextOpen((o) => !o)}
              disabled={busy}
              aria-expanded={textOpen}
              title="Text this client from the main number"
              style={{
                padding: '7px 14px', background: C.panel2, color: C.text,
                border: '1px solid var(--crm-comm-text-line)', borderRadius: '7px', cursor: 'pointer', fontSize: '12px', fontWeight: 600,
                display: 'inline-flex', alignItems: 'center', gap: '6px',
              }}
            >
              <Icons.message size={14} /> Text
            </button>
          )}
        </div>
      </div>

      {textOpen && ringCentralEnabled && (
        <TextCompose
          leadId={leadId}
          phone={textsData?.phone ?? leadPhone}
          canText={!!textsData?.can_text}
          optedOut={!!textsData?.opted_out}
          configured={!!textsData?.configured}
          loading={!textsData && !textsError}
          maxLength={textsData?.max_length ?? 1000}
          onClose={() => setTextOpen(false)}
          onSent={() => { refreshTexts(); refreshActivity(); }}
        />
      )}

      {/* Compose / reply */}
      {compose && (
        <div ref={composeRef}>
        <ComposeEmail
          leadId={leadId}
          lead={{ email: leadEmail }}
          capability={capability ?? null}
          replyTo={compose.reply}
          onClose={() => setCompose(null)}
          onSent={() => { refreshEmail(); refreshActivity(); }}
        />
        </div>
      )}

      {/* A SOURCE THAT FAILED SAYS SO. It never silently contributes zero rows —
          see the note beside `failed` above. */}
      {failed.map((f) => (
        <div
          key={f.label}
          style={{ display: 'flex', alignItems: 'center', gap: '10px', background: `${C.amberTint}`, border: `1px solid ${C.amberSoftLine}`, color: C.amber, borderRadius: '8px', padding: '8px 11px', marginBottom: '8px', fontSize: '12px', flexWrap: 'wrap' }}
        >
          <span style={{ flex: 1, lineHeight: 1.5 }}>
            <strong>{f.label}</strong> could not be loaded, so this feed is incomplete — what you see below is
            not everything on this file. ({f.error})
          </span>
          <button
            onClick={f.retry}
            style={{ padding: '4px 11px', background: C.panel2, border: `1px solid ${C.amberLine}`, color: C.amber, borderRadius: '6px', cursor: 'pointer', fontSize: '11px', fontWeight: 600 }}
          >
            Try again
          </button>
        </div>
      ))}

      {/* Filter chips — All / Communication / Notes */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px', flexWrap: 'wrap' }}>
        {([
          ['all', 'All'], ['comm', 'Communication'], ['notes', 'Notes'],
        ] as const).map(([key, label]) => (
          <button
            key={key}
            onClick={() => chooseFilter(key)}
            style={{
              ...chip(filter === key),
              border: `1px solid ${filter === key ? C.accentLine : C.border}`,
            }}
          >
            {label}
            {/* An em-dash, not 0, when the source behind this chip is unavailable.
                Zero is a claim; "—" is the absence of one. */}
            <span style={{ color: filter === key ? C.accent : C.dim, fontSize: '10px' }}>
              {key === 'comm' && (emailError || textsError) ? '—' : key === 'notes' && activityError ? '—' : counts[key]}
            </span>
          </button>
        ))}
      </div>

      {/* Feed */}
      <div ref={feedRef} style={{ display: 'flex', flexDirection: 'column', gap: '8px', overflowAnchor: 'none' }}>
        {visibleRows.length === 0 && (
          <div style={{ textAlign: 'center', padding: '24px', color: C.dim, fontSize: '12px' }}>
            {rows.length === 0
              ? 'No activity yet.'
              : `Nothing under ${filter === 'comm' ? 'Communication' : 'Notes'} — ${rows.length} other entr${rows.length === 1 ? 'y' : 'ies'} on this file.`}
          </div>
        )}
        {visibleRows.map((r) => <div key={r.id} data-feed-id={r.id}>{(() => {
          if (r.kind === 'email') {
            return (
              <EmailRow
                key={r.id}
                m={r.data}
                tz={tz}
                open={openEmail === r.id}
                onToggle={() => setOpenEmail((cur) => (cur === r.id ? null : r.id))}
                onMarkRead={() => markRead(r.data.linkId)}
                canReply={capability?.canSend !== false}
                replyReason={capability?.reason ?? ''}
                onReply={(all) => {
                  if (capability && !capability.canSend) { setErr(capability.reason); return; }
                  scrollToCompose.current = true;
                  setCompose({ reply: { message: r.data, all } });
                }}
              />
            );
          }
          if (r.kind === 'send') {
            return (
              <CrmSendRow
                key={r.id}
                a={r.data}
                send={r.send}
                tz={tz}
                repName={r.data ? userName(r.data.created_by_user_id) : userName(r.send?.sentBy)}
              />
            );
          }
          if (r.kind === 'text') {
            return (
              <TextRow
                key={r.id}
                m={r.data}
                tz={tz}
                elsewhere={!!r.data.lead_id && r.data.lead_id !== leadId}
                unread={!!r.data.unread && !readRc.has(`text:${r.data.id}`)}
                onMarkRead={() => markRcRead('text', r.data.id)}
              />
            );
          }
          if (r.kind === 'call') {
            return <CallRow key={r.id} a={r.data} repName={userName(r.data.created_by_user_id)} tz={tz}
                            unread={!!r.data.call?.unread && !readRc.has(`call:${r.data.call.id}`)}
                            onMarkRead={r.data.call ? () => markRcRead('call', r.data.call.id) : undefined} />;
          }
          if (r.kind === 'activity') {
            return <ActivityRow key={r.id} a={r.data} repName={userName(r.data.created_by_user_id)} onPin={() => togglePin(r.data)} tz={tz} />;
          }
          return (
            <FollowupRow
              key={r.id}
              f={r.data}
              users={users}
              currentUserId={user?.id}
              tz={tz}
              optimisticDone={!!optimisticDone[r.data.id]}
              onPin={() => toggleFollowupPin(r.data)}
              onComplete={() => completeFu(r.data)}
              onReassign={(uid) => reassignFu(r.data, uid)}
            />
          );
        })()}</div>)}
      </div>

      {showFu && <FollowUpModal busy={busy} users={users} defaultRep={user?.id ?? ''} defaultDate={myToday} onClose={() => setShowFu(false)} onSave={scheduleFollowUp} />}
    </div>
  );
}

const fieldInput: React.CSSProperties = { width: '100%', padding: '7px 9px', background: C.panel2, border: `1px solid ${C.border}`, color: C.text, borderRadius: '6px', fontSize: '12px', boxSizing: 'border-box' };

function FollowUpModal({ busy, users, defaultRep, defaultDate, onClose, onSave }: { busy: boolean; users?: any[]; defaultRep: string; defaultDate: string; onClose: () => void; onSave: (d: any) => void }) {
  const [f, setF] = useState({ follow_up_date: defaultDate, follow_up_time: '', description: '', assigned_to_user_id: defaultRep, pinned: false });
  const input = fieldInput;
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'var(--crm-overlay)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: '12px', padding: '20px', width: '420px', maxWidth: '92vw' }}>
        <div style={{ fontSize: '14px', fontWeight: 700, marginBottom: '14px', color: C.text }}>Schedule follow-up</div>
        <div style={{ display: 'grid', gap: '10px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}><span style={{ fontSize: '10px', color: C.muted }}>Date *</span><input type="date" value={f.follow_up_date} onChange={(e) => setF({ ...f, follow_up_date: e.target.value })} style={input} /></label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}><span style={{ fontSize: '10px', color: C.muted }}>Time</span><input type="time" value={f.follow_up_time} onChange={(e) => setF({ ...f, follow_up_time: e.target.value })} style={input} /></label>
          </div>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
            <span style={{ fontSize: '10px', color: C.muted }}>Assigned rep</span>
            <select value={f.assigned_to_user_id} onChange={(e) => setF({ ...f, assigned_to_user_id: e.target.value })} style={input}>
              {(users ?? []).map((u) => <option key={u.id} value={u.id}>{`${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() || u.email}</option>)}
            </select>
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}><span style={{ fontSize: '10px', color: C.muted }}>Description / notes</span><textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} style={{ ...input, minHeight: '56px', resize: 'vertical' }} /></label>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', cursor: 'pointer' }}>
            <input type="checkbox" checked={f.pinned} onChange={(e) => setF({ ...f, pinned: e.target.checked })} style={{ marginTop: '2px' }} />
            <span>
              <span style={{ fontSize: '12px', color: C.text }}>Pin to calendar</span>
              <span style={{ display: 'block', fontSize: '10px', color: C.dim }}>Pinned follow-ups appear on the calendar for visibility</span>
            </span>
          </label>
        </div>
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '16px' }}>
          <button onClick={onClose} style={{ padding: '8px 14px', background: C.panel2, color: C.muted, border: `1px solid ${C.border}`, borderRadius: '7px', cursor: 'pointer', fontSize: '12px' }}>Cancel</button>
          <button onClick={() => f.follow_up_date && onSave(f)} disabled={busy || !f.follow_up_date} style={{ padding: '8px 14px', background: C.accentFill, color: 'var(--crm-on-fill)', border: 'none', borderRadius: '7px', cursor: 'pointer', fontSize: '12px', fontWeight: 600, opacity: busy || !f.follow_up_date ? 0.5 : 1 }}>Schedule</button>
        </div>
      </div>
    </div>
  );
}

function PinBtn({ pinned, onClick, title }: { pinned: boolean; onClick: () => void; title: string }) {
  return (
    <button onClick={onClick} title={title} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px', color: pinned ? C.amber : C.dim, padding: '0 2px' }}>
      {pinned ? <Icons.pinned size={13} /> : <Icons.unpinned size={13} />}
    </button>
  );
}

function ActivityRow({ a, repName, onPin, tz }: { a: any; repName: string; onPin: () => void; tz: string }) {
  const isNote = a.activity_type === 'note';

  /**
   * WHO DID THIS — read from actor_type, which migration 029 added and nothing
   * has ever rendered.
   *
   * The actor came only from created_by_user_id, which is null for anything the
   * client did, so every client action showed with no actor at all: a rep's
   * signature carried their name and a client's carried nothing, and the
   * difference between "we signed this" and "they signed this" survived only as
   * prose inside the description. A blank is not a quieter way of saying
   * "client" — it reads as a row nobody owns.
   */
  const actor =
    a.actor_type === 'client' ? 'Client'
    : a.actor_type === 'system' ? 'System'
    : repName;
  const pinned = isNote && !!a.pinned;
  const accent = pinned ? C.amber : isNote ? C.accent : C.dim;
  const cms: string[] = Array.isArray(a.contact_methods) ? a.contact_methods : [];
  return (
    <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderLeft: `3px solid ${accent}`, borderRadius: '8px', padding: '9px 11px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
        {/* The LABEL, not the column value. This printed a.activity_type raw, so
            the feed announced "STAGE_CHANGE" and "FOLLOW_UP_COMPLETED" on a page
            reps read all day. One shared map now, so it cannot drift again. */}
        <span style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', color: isNote ? C.accent : C.muted, letterSpacing: '0.4px' }}>{activityTypeLabel(a.activity_type)}</span>
        {pinned && <span style={{ fontSize: '9px', fontWeight: 700, color: C.amber, background: `${C.amberTint}`, padding: '1px 6px', borderRadius: '4px' }}>PINNED</span>}
        <span style={{ fontSize: '10px', color: C.dim, fontFamily: C.mono }}>{formatDateTimeInTz(a.created_at, tz)}{actor ? ` · ${actor}` : ''}</span>
        {cms.map((m) => {
          const meta = METHODS.find((x) => x.key === m);
          return meta ? <span key={m} title={meta.label} style={{ fontSize: '10px', color: C.muted, background: C.panel2, border: `1px solid ${C.border}`, borderRadius: '4px', padding: '0 5px' }}><span style={{display:"inline-flex",alignItems:"center",gap:"3px"}}><meta.Icon size={11} /> {meta.label}</span></span> : null;
        })}
        {isNote && <span style={{ marginLeft: 'auto' }}><PinBtn pinned={pinned} onClick={onPin} title={pinned ? 'Unpin' : 'Pin to top'} /></span>}
      </div>
      <div style={{ fontSize: '12px', color: C.text, whiteSpace: 'pre-wrap' }}>{a.description}</div>
    </div>
  );
}

function FollowupRow({
  f, users, currentUserId, tz, optimisticDone, onPin, onComplete, onReassign,
}: {
  f: any; users?: any[]; currentUserId?: string; tz: string; optimisticDone: boolean;
  onPin: () => void; onComplete: () => void; onReassign: (userId: string) => void;
}) {
  const done = !!f.completed || optimisticDone;
  const nameOf = (id: string | null | undefined) => {
    const u = users?.find((x) => x.id === id);
    return u ? `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() || u.email : '';
  };
  const assigneeName = nameOf(f.assigned_to_user_id);
  const isMine = f.assigned_to_user_id === currentUserId;

  return (
    <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderLeft: `3px solid ${f.pinned ? C.amberLine : C.green}`, borderRadius: '8px', padding: '9px 11px', opacity: done ? 0.65 : 1 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', color: C.green, letterSpacing: '0.4px' }}>follow-up</span>
        <span style={{ fontSize: '9px', fontWeight: 700, color: done ? C.dim : C.amber, background: `${tintOf(done ? C.dim : C.amber, 13)}`, padding: '1px 6px', borderRadius: '4px' }}>{done ? 'COMPLETED' : 'SCHEDULED'}</span>
        <span style={{ fontSize: '10px', color: C.dim, fontFamily: C.mono }}>{formatDate(f.follow_up_date)}{f.follow_up_time ? ` ${f.follow_up_time}` : ''}</span>
        {/* Assignee shown whenever it's someone other than the viewer. */}
        {!isMine && assigneeName && (
          <span style={{ fontSize: '9px', fontWeight: 700, color: C.accent, background: `${C.accentTint}`, padding: '1px 6px', borderRadius: '4px' }}>{assigneeName}</span>
        )}
        <button onClick={onPin} title={f.pinned ? 'Hide from calendar' : 'Show on calendar'} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px', color: f.pinned ? C.amber : C.dim }}>
          {f.pinned ? <Icons.bellOn size={13} /> : <Icons.bellOff size={13} />}
        </button>
      </div>
      <div style={{ fontSize: '12px', color: C.text }}>{f.description || 'Follow-up'}</div>

      {done ? (
        <div style={{ fontSize: '10px', color: C.dim, marginTop: '6px', fontFamily: C.mono }}>
          {f.completed_by ? `Completed by ${nameOf(f.completed_by) || 'a user'}` : 'Completed'}
          {f.completed_at ? ` · ${formatDateTimeInTz(f.completed_at, tz)}` : ''}
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px', flexWrap: 'wrap' }}>
          <button
            onClick={onComplete}
            style={{ padding: '5px 12px', background: C.panel2, border: `1px solid ${C.greenSoftLine}`, color: C.green, borderRadius: '6px', cursor: 'pointer', fontSize: '11px', fontWeight: 600 }}
          >
            <span style={{display:"inline-flex",alignItems:"center",gap:"4px"}}><Icons.check size={13} /> Mark complete</span>
          </button>
          <label style={{ fontSize: '10px', color: C.dim, display: 'flex', alignItems: 'center', gap: '5px' }}>
            Assignee
            <select
              value={f.assigned_to_user_id ?? ''}
              onChange={(e) => onReassign(e.target.value)}
              style={{ padding: '4px 6px', background: C.panel2, border: `1px solid ${C.border}`, color: C.text, borderRadius: '5px', fontSize: '11px', cursor: 'pointer' }}
            >
              {(users ?? []).map((u) => <option key={u.id} value={u.id}>{`${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() || u.email}</option>)}
            </select>
          </label>
        </div>
      )}
    </div>
  );
}


/**
 * ONE EMAIL, IN THE FEED.
 *
 * Collapsed it is a single line a rep can scan past: which way it went, who
 * wrote it, the subject, a snippet, a paperclip if there is one, and the time in
 * their own zone. Expanded it is the message.
 *
 * Collapsed by DEFAULT and one open at a time, because a file with 122 messages
 * on it would otherwise be a wall of iframes — and every open frame is a
 * document the browser has to lay out.
 *
 * Reply and Reply all land here in G1c. They are deliberately absent rather than
 * present-and-disabled: a greyed button that never works for a week reads as a
 * broken feature, not a coming one.
 */
function EmailRow({
  m, tz, open, onToggle, onMarkRead, canReply, replyReason, onReply,
}: {
  m: any; tz: string; open: boolean; onToggle: () => void; onMarkRead: () => void;
  canReply: boolean; replyReason: string; onReply: (all: boolean) => void;
}) {
  const inbound = m.direction === 'inbound';
  const who = inbound ? (m.fromName || m.fromAddress || 'Client') : (m.to ?? []).join(', ') || m.fromAddress;
  const unseen = inbound && !m.seenAt;

  return (
    /* UNREAD IS A WHOLE ROW, not a dot. A six-pixel dot in a feed of forty
       entries is not something anyone finds by scanning; the tinted background
       and the accent border are. The id is the scroll target the Messages
       dropdown aims at. */
    <div
      id={`email-${m.id}`}
      style={commBox('email', unseen ? { background: `${C.accentTint}`, border: `1px solid ${C.accentSoftLine}`, borderLeft: `3px solid ${COMM.email.fg}` } : undefined)}
    >
      <div
        onClick={onToggle}
        style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', flexWrap: 'wrap' }}
      >
        <CommTag ch="email" dir={inbound ? 'in' : 'out'} />
        {unseen && <span title="Unread" style={{ width: '6px', height: '6px', borderRadius: '50%', background: C.accentFill, flexShrink: 0 }} />}
        <span style={{ fontSize: '10px', color: C.dim, fontFamily: C.mono, minWidth: 0, overflowWrap: 'anywhere' }}>
          {formatDateTimeInTz(m.sentAt, tz)} · {who}
        </span>
        {(m.attachments ?? []).length > 0 && (
          <span title={`${m.attachments.length} attachment${m.attachments.length === 1 ? '' : 's'}`} style={{ display: 'inline-flex', color: C.muted }}>
            <Icons.download size={12} />
          </span>
        )}
        <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
          {unseen && (
            <button
              onClick={(e) => { e.stopPropagation(); onMarkRead(); }}
              title="Mark this email read"
              style={{ padding: '3px 9px', background: C.panel2, border: `1px solid ${C.accentSoftLine}`, color: C.accent, borderRadius: '6px', cursor: 'pointer', fontSize: '10.5px', fontWeight: 600 }}
            >
              Mark read
            </button>
          )}
          <span style={{ color: C.dim, display: 'inline-flex' }}>
            {open ? <Icons.chevronUp size={14} /> : <Icons.chevronDown size={14} />}
          </span>
        </span>
      </div>

      <div onClick={open ? undefined : onToggle} style={{ cursor: open ? 'default' : 'pointer', marginTop: '4px' }}>
        <div style={{ fontSize: '12px', color: C.text, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: open ? 'normal' : 'nowrap' }}>
          {m.subject || '(no subject)'}
        </div>
        {!open && (
          <div style={{ fontSize: '11px', color: C.dim, marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {m.snippet ?? ''}
          </div>
        )}
      </div>

      {open && (
        <div style={{ marginTop: '8px' }}>
          <div style={{ fontSize: '11px', color: C.dim, fontFamily: C.mono, marginBottom: '8px', wordBreak: 'break-word' }}>
            from {m.fromAddress || '—'} · to {(m.to ?? []).join(', ') || '—'}
            {(m.cc ?? []).length > 0 && <> · cc {(m.cc ?? []).join(', ')}</>}
          </div>
          <EmailBody body={m.body} />
          <Attachments attachments={m.attachments ?? []} />

          {/* Reply all appears only where there IS anyone else on the message.
              A second button that silently does the same as the first teaches
              people not to read buttons. */}
          <div style={{ display: 'flex', gap: '7px', marginTop: '10px', paddingTop: '10px', borderTop: `1px solid ${C.panel2}` }}>
            <button
              onClick={() => onReply(false)}
              title={canReply ? 'Reply from your own Gmail' : replyReason}
              style={{ padding: '5px 12px', background: C.panel2, border: `1px solid ${canReply ? `${C.accentSoftLine}` : C.border}`, color: canReply ? C.text : C.dim, borderRadius: '6px', cursor: 'pointer', fontSize: '11px', fontWeight: 600 }}
            >
              Reply
            </button>
            {[...(m.to ?? []), ...(m.cc ?? [])].length > 1 && (
              <button
                onClick={() => onReply(true)}
                title={canReply ? 'Reply to everyone on this message' : replyReason}
                style={{ padding: '5px 12px', background: C.panel2, border: `1px solid ${C.border}`, color: canReply ? C.muted : C.dim, borderRadius: '6px', cursor: 'pointer', fontSize: '11px' }}
              >
                Reply all
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * A CRM SEND — the estimate, the invoice, the new-lead acknowledgement.
 *
 * Two ways in, one row out. Usually `a` is the activity entry that already
 * described the send in prose and `send` is the email_log row it names, and this
 * renders the prose ENRICHED with delivery state, clicks and the client link.
 * When `a` is null the send wrote no activity line — which is what a FAILED send
 * does, because logSystem sits inside `if (result.ok)` — and the row stands on
 * its own so the failure is on the file rather than only in the Audit Report.
 *
 * There is no body to show either way: these are rendered and transmitted by
 * Resend, so no copy exists on our side. The row says so rather than leaving an
 * empty panel that reads as a loading failure.
 */
function CrmSendRow({ a, send, tz, repName }: { a: any | null; send: any; tz: string; repName: string }) {
  const st = sendStatus(send?.status);
  const failed = ['bounced', 'failed', 'complained'].includes(String(send?.status));

  return (
    <div style={commBox('email', failed ? { borderLeft: `3px solid ${C.redLine}` } : undefined)}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', flexWrap: 'wrap' }}>
        <CommTag ch="email" dir="out" label={emailTypeLabel(send?.emailType)} />
        <span style={{ fontSize: '9px', fontWeight: 700, color: st.color, background: `${tintOf(st.color, 13)}`, padding: '1px 6px', borderRadius: '4px' }}>
          {st.label}
        </span>
        <span style={{ fontSize: '10px', color: C.dim, fontFamily: C.mono }}>
          {formatDateTimeInTz(send?.at ?? a?.created_at, tz)}{repName ? ` · ${repName}` : ''}
        </span>
      </div>

      {/* The prose line when there is one; otherwise a plain statement of what
          was sent and to whom. Never a blank where the description should be. */}
      <div style={{ fontSize: '12px', color: C.text, whiteSpace: 'pre-wrap' }}>
        {a?.description ?? `${emailTypeLabel(send?.emailType)} sent to ${send?.recipient ?? 'the client'}.`}
      </div>

      <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', marginTop: '6px' }}>
        <span style={{ fontSize: '10px', color: C.dim, fontFamily: C.mono }}>{send?.recipient}</span>
        {send?.clickedAt && (
          <span title="Link clicks are reliable; opens are not tracked." style={{ fontSize: '10px', color: 'var(--crm-cyan)', display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
            <Icons.click size={11} /> Clicked{send.clickCount > 1 ? ` ·${send.clickCount}` : ''}
          </span>
        )}
        {send?.portalPath && (
          <a href={send.portalPath} target="_blank" rel="noopener noreferrer" style={{ fontSize: '10px', color: C.accent, display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
            <Icons.openExternal size={11} /> Open what the client sees
          </a>
        )}
      </div>

      {(send?.bounceReason || send?.error) && (
        <div style={{ marginTop: '6px', fontSize: '11px', color: failed ? C.red : C.amber, lineHeight: 1.5 }}>
          {send.bounceReason || send.error}
        </div>
      )}

      {!a && (
        <div style={{ marginTop: '6px', fontSize: '10px', color: C.dim, lineHeight: 1.5 }}>
          This send has no activity line of its own — failed and blocked sends never wrote one. It is shown
          here so the attempt is on the file. The full record, and the re-send, are in the Audit Report.
        </div>
      )}
    </div>
  );
}


/**
 * ONE TEXT, IN THE FEED — the whole message, which way it went, who sent it,
 * when, and (outbound) whether it was delivered. A text filed on another of the
 * client's jobs says so. An inbound text still unread in Messages
 * carries the same Mark read as the bell.
 */
function TextRow({ m, tz, elsewhere, unread, onMarkRead }: {
  m: LeadTextMessage; tz: string; elsewhere: boolean; unread: boolean; onMarkRead?: () => void;
}) {
  const inbound = m.direction === 'Inbound';
  const who = inbound ? 'Client'
    : m.sent_via === 'auto' ? 'Auto-text'
    : `${m.sender || 'BVL'}${m.sent_via === 'ringcentral_app' ? ' · RingCentral app' : ''}`;
  const status = inbound ? null
    : m.status === 'DeliveryFailed' || m.status === 'SendingFailed' ? { text: 'Not delivered', color: C.red }
    : m.status === 'Delivered' ? { text: 'Delivered', color: 'var(--crm-comm-text)' }
    // Anything stored was accepted by RingCentral: Queued reads as Sent.
    : m.status === 'Queued' || m.status === 'Sent' ? { text: 'Sent', color: C.dim }
    : null;
  return (
    <div id={`rc-text-${m.id}`} style={commBox('text')}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', flexWrap: 'wrap' }}>
        {unread && <span title="Unread" style={{ width: '7px', height: '7px', borderRadius: '50%', background: 'var(--crm-accent-fill)', flexShrink: 0 }} />}
        <CommTag ch="text" dir={inbound ? 'in' : 'out'} />
        <span style={{ fontSize: '10px', color: C.dim, fontFamily: C.mono, minWidth: 0, overflowWrap: 'anywhere' }}>
          {formatDateTimeInTz(m.at, tz)} · {who}
        </span>
        {status && <span style={{ fontSize: '9px', fontWeight: 700, color: status.color }}>{status.text}</span>}
        {elsewhere && (
          <span title="This text is filed on another of the client's jobs"
                style={{ fontSize: '9px', fontWeight: 700, color: C.muted, background: C.panel, border: `1px solid ${C.border}`, padding: '0 6px', borderRadius: '4px' }}>
            on job {m.lead_job_no ?? '—'}
          </span>
        )}
        {unread && onMarkRead && <MarkReadBtn onClick={onMarkRead} fg="var(--crm-comm-text)" />}
      </div>
      <div style={{ fontSize: '13px', color: C.text, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
        {m.body || <span style={{ color: C.dim }}>(no text)</span>}
      </div>
    </div>
  );
}

/**
 * A RingCentral call line, shown as a call: in / out, missed, and the words the
 * server wrote (direction, length, outcome, the rep's notes). A missed call
 * still unread in Messages carries Mark read.
 */
function CallRow({ a, repName, tz, unread, onMarkRead }: {
  a: any; repName: string; tz: string; unread: boolean; onMarkRead?: () => void;
}) {
  const call = a.call ?? null;
  const dir: 'in' | 'out' | null = call ? (call.direction === 'Inbound' ? 'in' : 'out') : null;
  const missed = call?.result === 'missed';
  const actor = a.actor_type === 'system' ? 'System' : repName;
  return (
    <div id={call ? `rc-call-${call.id}` : undefined} style={commBox('call', missed ? { borderLeft: `3px solid ${C.redLine}` } : undefined)}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', flexWrap: 'wrap' }}>
        {unread && <span title="Unread" style={{ width: '7px', height: '7px', borderRadius: '50%', background: 'var(--crm-accent-fill)', flexShrink: 0 }} />}
        <CommTag ch="call" dir={dir} label={missed ? 'Missed call' : 'Call'} />
        <span style={{ fontSize: '10px', color: C.dim, fontFamily: C.mono, minWidth: 0, overflowWrap: 'anywhere' }}>
          {formatDateTimeInTz(a.created_at, tz)}{actor ? ` · ${actor}` : ''}
        </span>
        {unread && onMarkRead && <MarkReadBtn onClick={onMarkRead} fg="var(--crm-comm-call)" />}
      </div>
      <div style={{ fontSize: '12px', color: C.text, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{a.description}</div>
    </div>
  );
}
